'use client'

import { useMemo, useState } from 'react'

import { cn } from '@/lib/utils'
import { faires, flagshipNotes } from '@/content/renaissance-faires/data'
import type { Region, Season } from '@/content/renaissance-faires/types'

import { SectionHeading } from './heraldry'
import { scheduleOf } from './schedule'

/**
 * A short flagship list doesn't need the dataset's finer regions — they roll up
 * into four quadrants so most filter picks return more than one card.
 */
const ZONES = ['East', 'South', 'Midwest', 'West'] as const
type Zone = (typeof ZONES)[number]

function zoneOf(region: Region): Zone {
    switch (region) {
        case 'Northeast':
            return 'East'
        case 'Mid-Atlantic & Southeast':
        case 'South Central':
            return 'South'
        case 'Great Lakes':
        case 'Plains & Upper Midwest':
            return 'Midwest'
        default:
            return 'West'
    }
}

const SEASONS: { label: string; value: Season | null }[] = [
    { label: 'All seasons', value: null },
    { label: 'Spring', value: 'spring' },
    { label: 'Summer', value: 'summer' },
    { label: 'Fall', value: 'fall' },
    { label: 'Winter', value: 'winter' },
]

const SEASON_STYLES: Record<Season, string> = {
    spring: 'bg-[#fcf3ca] text-[#876102]',
    summer: 'bg-[#eef7d6] text-[#4e6b00]',
    fall: 'bg-[#ece6fa] text-[#523a9e]',
    winter: 'bg-[#e6f9fb] text-[#00606b]',
}

/**
 * "The big ones": the long-running faires on permanent grounds, as a first-faire
 * shortlist. Explicitly not a ranking — the brief forbids ranking faires, which
 * is also why the notes carry a character fact or the getting-there catch rather
 * than attendance figures.
 *
 * This section carries its own season and region filters, independent of the
 * finder's, because it's browsed rather than searched.
 */
export const TheBigOnes = ({ today }: { today: string }) => {
    const [season, setSeason] = useState<Season | null>(null)
    const [zone, setZone] = useState<Zone | null>(null)

    const flagships = useMemo(() => faires.filter((faire) => faire.isFlagship), [])
    const shown = flagships.filter((faire) => {
        if (season && !faire.seasons.includes(season)) {
            return false
        }
        if (zone && zoneOf(faire.region) !== zone) {
            return false
        }
        return true
    })
    const availableZones = ZONES.filter((candidate) =>
        flagships.some((faire) => zoneOf(faire.region) === candidate),
    )
    const isFiltered = season !== null || zone !== null

    const chipClass = (active: boolean) =>
        cn(
            'h-8 cursor-pointer rounded-[2px] border px-3 font-sans text-[13px] font-medium transition-colors duration-150',
            active
                ? 'border-[var(--rf-blu-700)] bg-[var(--rf-blu-700)] text-white'
                : 'border-[var(--rf-border-dark)] bg-white text-[var(--rf-fg-2)] hover:bg-[var(--rf-smoke-25)]',
        )

    return (
        <section id="big-ones" className="mx-auto max-w-[1600px] scroll-mt-4 px-[18px] py-15">
            <SectionHeading>The big ones</SectionHeading>
            <p className="m-0 mt-1.5 mb-[18px] max-w-[720px] text-[15px] text-pretty text-[var(--rf-fg-2)]">
                The long-running, well-known ones, most on permanent grounds they&rsquo;ve built up
                over decades. If it&rsquo;s your first faire, start here — each note is what&rsquo;s
                worth knowing before you go, including how to actually get there. Not a ranking.
            </p>

            <div className="mb-[18px] flex flex-wrap items-center gap-3">
                <div
                    className="flex flex-wrap gap-[3px]"
                    role="group"
                    aria-label="Filter the big ones by season"
                >
                    {SEASONS.map((option) => (
                        <button
                            key={option.label}
                            type="button"
                            aria-pressed={season === option.value}
                            onClick={() => setSeason(option.value)}
                            className={chipClass(season === option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                <select
                    value={zone ?? ''}
                    aria-label="Filter the big ones by region"
                    onChange={(event) => setZone((event.target.value as Zone) || null)}
                    className="h-8 max-w-[240px] rounded-[3px] border border-[var(--rf-border-dark)] bg-white px-2 font-sans text-[13px] text-[var(--rf-fg)]"
                >
                    <option value="">All regions</option>
                    {availableZones.map((candidate) => (
                        <option key={candidate} value={candidate}>
                            {candidate}
                        </option>
                    ))}
                </select>
                {isFiltered && (
                    <span className="flex items-center gap-2.5 text-[13px] text-[var(--rf-fg-3)]">
                        {shown.length} of {flagships.length} showing
                        <button
                            type="button"
                            onClick={() => {
                                setSeason(null)
                                setZone(null)
                            }}
                            className="h-8 cursor-pointer rounded-[3px] border-none bg-transparent px-2.5 font-sans text-[13px] font-semibold text-[var(--rf-blu-700)] hover:underline"
                        >
                            Clear
                        </button>
                    </span>
                )}
            </div>

            {shown.length === 0 ? (
                <div className="rounded-lg border border-dashed border-[var(--rf-border-dark)] p-6 text-center text-[15px] text-[var(--rf-fg-2)]">
                    No big ones match that pairing. Try a broader season or another region.
                </div>
            ) : (
                <ul className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {shown.map((faire) => {
                        const schedule = scheduleOf(faire, today)
                        return (
                            <li
                                key={faire.id}
                                className="flex flex-col rounded-lg border border-[var(--rf-border)] bg-white p-4 shadow-[var(--rf-shadow-1)]"
                            >
                                <div className="mb-1 flex flex-wrap items-baseline gap-2">
                                    {faire.url ? (
                                        <a
                                            href={faire.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-base leading-[1.25] font-bold text-[var(--rf-fg)] hover:text-[var(--rf-blu-700)]"
                                        >
                                            {faire.name}
                                        </a>
                                    ) : (
                                        <span className="text-base leading-[1.25] font-bold text-[var(--rf-fg)]">
                                            {faire.name}
                                        </span>
                                    )}
                                    <span
                                        className={cn(
                                            'rounded-[2px] px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap capitalize',
                                            SEASON_STYLES[faire.seasons[0]],
                                        )}
                                    >
                                        {faire.seasons.join(' & ')}
                                    </span>
                                </div>
                                <div className="text-[13px] text-[var(--rf-fg-2)]">
                                    {faire.city}, {faire.state}
                                </div>
                                {flagshipNotes[faire.id] && (
                                    <p className="m-0 mt-2.5 text-sm leading-[1.5] text-pretty text-[var(--rf-fg-2)]">
                                        {flagshipNotes[faire.id]}
                                    </p>
                                )}
                                <div className="mt-2.5 text-[13px] text-[var(--rf-fg-3)]">
                                    {schedule.dateLine}
                                </div>
                                <a
                                    href={faire.tripUrl}
                                    className="mt-2.5 text-[13px] font-semibold text-[var(--rf-blu-700)] hover:underline"
                                >
                                    Get to {faire.nearestCityShort} →
                                </a>
                            </li>
                        )
                    })}
                </ul>
            )}
        </section>
    )
}
